import {createSelector} from 'reselect'
import keyBy from 'lodash/keyBy.js'
import sortBy from 'lodash/sortBy.js'
import get from 'lodash/get.js'

import {getState, setForm, updateForm, formsSelector} from '../store.js'
import apiverson from '../common/apiverson.js'
import {fetchAllApiverson} from '../common/fetchAll.js'
import {TAG_TYPE__ORDER} from '../common/constants/Tags.js'
import {showGlobalError} from '../ordoro/GlobalErrorMessage.js'
import {
  allOrdersSelector,
  clearPendingTagAfterError,
  markTagPending,
} from './orders.js'
import {refreshOrderList} from '../ordoro/OrderListPage/orderListActions.js'
import {tokenizeOptions, getNounsFromValue} from '../common/tokenizeOptions.js'
import {buildPath} from '../common/querystring.js'

export const ORDER_TAGS = 'ORDER_TAGS'

export function setOrderTags(orderTags) {
  setForm(ORDER_TAGS, keyBy(orderTags, 'id'))
}

export function updateOrderTag(orderTag) {
  updateForm(ORDER_TAGS, {[orderTag.id]: orderTag})
}

export function removeOrderTag(orderTag) {
  const orderTags = {...orderTagsSelector(getState())}

  delete orderTags[orderTag.id]

  setForm(ORDER_TAGS, orderTags)
}

export function orderTagsSelector(state) {
  return formsSelector(state)[ORDER_TAGS] || orderTagsSelector.default
}
orderTagsSelector.default = {}

export function orderTagSelector(state, {orderTagID}) {
  return orderTagsSelector(state)[orderTagID]
}

export function orderTagByNameSelector(state, {name}) {
  const foundTag = Object.values(orderTagsSelector(state)).find(
    (tag) => tag.name === name,
  )

  return foundTag || null
}

export const orderTagsSortedByNameSelector = createSelector(
  orderTagsSelector,
  (orderTags) => sortBy(orderTags, ({name}) => name.toLowerCase()),
)

export const orderTagOptionsSelector = createSelector(
  orderTagsSortedByNameSelector,
  (tags) =>
    tags.map((tag) => ({
      value: tag.id,
      display: tag.name,
      entity: tag,
      type: TAG_TYPE__ORDER,
      nouns: getNounsFromValue(tag.name),
    })),
)

export const orderTagOptionTokensSelector = createSelector(
  orderTagOptionsSelector,
  (tagOptions) => tokenizeOptions(tagOptions),
)

export function orderTagsHaveLoadedSelector(state) {
  return !!formsSelector(state)[ORDER_TAGS]
}

export async function getOrderTags() {
  const orderTags = await fetchAllApiverson('/order/tag', 'tag')

  setOrderTags(orderTags)
}

export async function saveOrderTag(name, color, id) {
  const tag = id ? orderTagSelector(getState(), {orderTagID: id}) : null
  const params = {name: name.trim(), color}

  const {json} = id
    ? await apiverson.put(buildPath(['order', 'tag', tag.name]), params)
    : await apiverson.post('/order/tag', params)

  updateOrderTag(json)

  if (id) {
    await refreshOrderList()
  }

  return json
}

export async function ensureTagExists(name, color) {
  // note: We don't want to force a color change. We should let customers keep
  // whatever tag colors they want
  const tag = orderTagByNameSelector(getState(), {name})

  if (tag) {
    return tag
  }

  const newTag = await saveOrderTag(name, color)

  return newTag
}

export async function deleteOrderTag(tagID) {
  const tag = orderTagSelector(getState(), {orderTagID: tagID})

  await apiverson.delete(buildPath(['order', 'tag', tag.name]))

  removeOrderTag(tag)
}

export async function addTagToOrders(orderTagID, orderNumbers) {
  const orders = allOrdersSelector(getState())
  const tag = orderTagSelector(getState(), {orderTagID})

  if (!tag) {
    return
  }

  const orderNumbersWithoutTag = orderNumbers.filter(
    (orderNumber) =>
      !get(orders, [orderNumber, 'tags'], []).find((t) => t.id === tag.id),
  )

  orderNumbersWithoutTag.map((orderNumber) =>
    markTagPending(orderNumber, tag, true),
  )

  try {
    await Promise.all(
      orderNumbersWithoutTag.map((orderNumber) =>
        apiverson.post(buildPath(['order', orderNumber, 'tag', tag.name])),
      ),
    )
  } catch (error) {
    showGlobalError(
      {
        summary: 'Error adding order tag.',
        details: error.error_message || error.message,
      },
      error,
    )

    orderNumbersWithoutTag.map((orderNumber) =>
      clearPendingTagAfterError(orderNumber, tag.id, true),
    )
  }
}

export async function removeTagFromOrders(orderTagID, orderNumbers) {
  const orders = allOrdersSelector(getState())
  const tag = orderTagSelector(getState(), {orderTagID})

  if (!tag) {
    return
  }

  const orderNumbersWithTag = orderNumbers.filter((orderNumber) =>
    get(orders, [orderNumber, 'tags'], []).find((t) => t.id === tag.id),
  )

  orderNumbersWithTag.map((orderNumber) =>
    markTagPending(orderNumber, tag, false),
  )

  try {
    await Promise.all(
      orderNumbersWithTag.map((orderNumber) =>
        apiverson.delete(buildPath(['order', orderNumber, 'tag', tag.name])),
      ),
    )
  } catch (error) {
    showGlobalError(
      {
        summary: 'Error removing order tag.',
        details: error.error_message || error.message,
      },
      error,
    )

    orderNumbersWithTag.map((orderNumber) =>
      clearPendingTagAfterError(orderNumber, tag.id, false),
    )
  }
}
